import * as T from '../Types';
import * as E from '../Enums';

export class Position {

    constructor(
        /** Internal position ID */
        public id: string,
        
        /** The current status of the position */
        public status: E.PositionStatus,
        /** The financial instrument associated with the order */
        public option: T.Option,
        /** The total number of contracts held in this position */
        public contractsHeld: number,
        /** The number of contracts available to trade (not locked in orders) */
        public contractsAvailable: number,

        public orderIds: string[],
        
        /** When the position was opened */
        public openedAt?: string,
        /** When the position was closed */
        public closedAt?: string,

        /** The most recent price of the position's symbol */
        public lastPrice?: number,

        /** The position's PnL stats */
        public stats?: {
            /** The average cost of all BUY fills () */
            avgUnitCost: number,
            /** The sum of all BUY fills */
            totalCost: number,
            /** The sum of all SELL fills */
            totalProceeds: number,
            /** The current market value of the position's held contracts */
            marketValue: number,
            /** The percentage of the portfolio that this position represents */
            percentOfPortfolio: number,
            /** The unrealized PnL of held contracts */
            openPnL: number,
            /** The realized PnL of closed contracts */
            closedPnL: number
        },

        /**
         * Whether the position data has become detached from the broker,
         * due to all remaining contracts being closed outside of this app
         */
        public detached: boolean = false
    ){}

    /**
     * Update position price and recalculate market-based stats (marketValue, openPnL, percentOfPortfolio).
     * Called when a quote update is received or position is fetched from broker.
     * 
     * @param newPrice Current market price per contract
     * @param portfolioValue Total portfolio value (for calculating percentOfPortfolio)
     */
    updateFromPriceUpdate(newPrice: number, portfolioValue: number): void {
        if (!this.stats) return;
        
        this.lastPrice = newPrice;
        
        // Calculate new market value (price * contracts held)
        const multiplier = this.option.type ? 100 : 1;
        this.stats.marketValue = Number((newPrice * this.contractsHeld * multiplier).toFixed(2));
        
        // percentOfPortfolio = marketValue / portfolioValue
        this.stats.percentOfPortfolio = portfolioValue > 0 
            ? Number(((this.stats.marketValue / portfolioValue) * 100).toFixed(2))
            : 0;
        
        // openPnL = marketValue - totalCost (unrealized gain/loss on held contracts)
        this.stats.openPnL = Number((this.stats.marketValue - this.stats.totalCost).toFixed(2));
    }

    /**
     * Record a BUY order fill. Updates avgUnitCost and totalCost.
     * 
     * @param quantity Number of contracts filled
     * @param fillPrice Average price per contract
     */
    recordBuyFill(quantity: number, fillPrice: number): void {
        if (!this.stats) return;
        
        const multiplier = this.option.type ? 100 : 1;
        const costOfThisFill = quantity * fillPrice * multiplier;
        
        // Update weighted average cost
        const totalCostBefore = this.stats.totalCost;
        const contractsHeldBefore = this.contractsHeld - quantity; // contractsHeld already increased
        
        if (contractsHeldBefore >= 0) {
            // Adding to position: weighted average
            const newTotalCost = totalCostBefore + costOfThisFill;
            this.stats.avgUnitCost = contractsHeldBefore > 0 
                ? Number((newTotalCost / (this.contractsHeld * multiplier)).toFixed(2))
                : Number(fillPrice.toFixed(2));
            this.stats.totalCost = Number(newTotalCost.toFixed(2));
        } else {
            // Closing a short position - this shouldn't happen in basic trading, but handle it
            this.stats.avgUnitCost = Number(fillPrice.toFixed(2));
        }
    }

    /**
     * Record a SELL order fill. Updates totalProceeds and closedPnL.
     * 
     * @param quantity Number of contracts sold
     * @param fillPrice Average price per contract
     */
    recordSellFill(quantity: number, fillPrice: number): void {
        if (!this.stats) return;
        
        const multiplier = this.option.type ? 100 : 1;
        const proceedsFromThisFill = quantity * fillPrice * multiplier;
        
        // Add to total proceeds
        this.stats.totalProceeds = Number((this.stats.totalProceeds + proceedsFromThisFill).toFixed(2));
        
        // Calculate realized PnL from this sell
        // Cost of contracts sold = avg cost per contract * quantity sold
        const costOfSoldContracts = this.stats.avgUnitCost * quantity * multiplier;
        const realizedPnLFromThisSell = proceedsFromThisFill - costOfSoldContracts;
        
        // Add to closed PnL
        this.stats.closedPnL = Number((this.stats.closedPnL + realizedPnLFromThisSell).toFixed(2));
    }

    /**
     * Update only the lastPrice from broker position data.
     * Do NOT overwrite stats, contractsHeld, or contractsAvailable - these are driven by order placement/fills.
     * 
     * @param brokerPosition Position data from broker
     */
    updatePriceFromBroker(brokerPosition: Position): void {
        if (!brokerPosition.lastPrice) return;
        // Only update lastPrice; all other fields are curated by order tracking
        this.lastPrice = brokerPosition.lastPrice;

        // TODO validate held contracts still matches broker data
    }
}